import { inject, Injectable } from '@angular/core'
import { apiEndpoint } from '@awork/environments/environment'
import { ApiClient } from '@awork/_shared/services/api-client/ApiClient'
import { BrowserService } from '@awork/_shared/services/browser-service/browser.service'
import { DesktopService } from '@awork/_shared/services/desktop-service/desktop.service'
import { getBrowserInfo } from '@awork/_shared/functions/browser-info'
import type { Observable } from 'rxjs'
import { from, of, concatMap, endWith, ignoreElements } from 'rxjs'

export enum FrontendMetric {
  ShakyToastShown = 'app.toast.error',
  BadRequestToastShown = 'app.toast.bad_request',
  LiveEditingLoadDurationMs = 'app.editor.live_editing_load_duration_ms',
  AITimeToFirstTokenMs = 'app.ai.time_to_first_token_ms'
}

export type MetricOperation = 'increment' | 'decrement' | 'set'

export interface MetricPostForm {
  name: FrontendMetric
  value: number
  operation?: MetricOperation
  tags?: string[]
}

export interface MetricsBatchPostForm {
  metrics: MetricPostForm[]
}

const MAX_METRICS_BATCH_SIZE = 100
const MAX_METRICS_QUEUE_SIZE = 1000
const METRICS_FLUSH_INTERVAL_MS = 10000

@Injectable({ providedIn: 'root' })
export class MetricsService {
  private apiClient = inject(ApiClient)
  private browserService = inject(BrowserService)
  private desktopService = inject(DesktopService)
  private readonly metricsUrl = `${apiEndpoint}/tracking/metrics`
  private metricsQueue: MetricPostForm[] = []
  private flushTimeoutId: ReturnType<typeof setTimeout> | null = null
  private flushInFlight = false
  private contextTags: string[] | null = null

  /**
   * Queues an increment frontend metric for batched sending.
   * @param {FrontendMetric} name - The metric name.
   * @param {number} value - The value to increment by.
   * @param {string[]} tags - Optional tags to attach to the metric.
   * @returns {Observable<void>} Completes after the metric is enqueued.
   */
  incrementMetric(name: FrontendMetric, value: number, tags?: string[]): Observable<void> {
    return this.trackMetric(name, value, 'increment', tags)
  }

  /**
   * Queues a decrement frontend metric for batched sending.
   * @param {FrontendMetric} name - The metric name.
   * @param {number} value - The value to decrement by.
   * @param {string[]} tags - Optional tags to attach to the metric.
   * @returns {Observable<void>} Completes after the metric is enqueued.
   */
  decrementMetric(name: FrontendMetric, value: number, tags?: string[]): Observable<void> {
    return this.trackMetric(name, value, 'decrement', tags)
  }

  /**
   * Queues a frontend metric set for batched sending.
   * @param {FrontendMetric} name - The metric name.
   * @param {number} value - The value to set.
   * @param {string[]} tags - Optional tags to attach to the metric.
   * @returns {Observable<void>} Completes after the metric is enqueued.
   */
  setMetric(name: FrontendMetric, value: number, tags?: string[]): Observable<void> {
    return this.trackMetric(name, value, 'set', tags)
  }

  /**
   * Queues a single metric with the specified operation for batched sending.
   * @param {FrontendMetric} name - The metric name.
   * @param {number} value - The metric value.
   * @param {MetricOperation} operation - The metric operation.
   * @param {string[]} tags - Optional tags to attach to the metric.
   * @returns {Observable<void>} Completes after the metric is enqueued.
   */
  trackMetric(name: FrontendMetric, value: number, operation: MetricOperation, tags?: string[]): Observable<void> {
    const metric: MetricPostForm = { name, value, operation }

    if (tags?.length) {
      metric.tags = tags
    }

    return this.trackMetrics([metric])
  }

  /**
   * Queues multiple metrics for batched sending.
   * @param {MetricPostForm[]} metrics - The metrics to track.
   * @returns {Observable<void>} Completes after the metrics are enqueued.
   */
  trackMetrics(metrics: MetricPostForm[]): Observable<void> {
    return this.enqueueMetrics(this.applyContextTags(metrics))
  }

  /**
   * Enqueues metrics for batched sending.
   * @param {MetricPostForm[]} metrics - The metrics to enqueue.
   * @returns {Observable<void>} Completes after the metrics are enqueued.
   */
  private enqueueMetrics(metrics: MetricPostForm[]): Observable<void> {
    if (!metrics.length) {
      return of(void 0)
    }

    const incoming = metrics.length > MAX_METRICS_QUEUE_SIZE ? metrics.slice(-MAX_METRICS_QUEUE_SIZE) : metrics
    const overflow = this.metricsQueue.length + incoming.length - MAX_METRICS_QUEUE_SIZE

    if (overflow > 0) {
      this.metricsQueue.splice(0, Math.min(overflow, this.metricsQueue.length))
    }

    this.metricsQueue.push(...incoming)

    if (!this.flushInFlight) {
      if (this.metricsQueue.length >= MAX_METRICS_BATCH_SIZE) {
        this.flushQueue()
      } else {
        this.scheduleFlush()
      }
    }

    return of(void 0)
  }

  /**
   * Returns the cached client context tags.
   * @returns {string[]} The client context tags.
   */
  private getContextTags(): string[] {
    if (this.contextTags) {
      return this.contextTags
    }

    this.contextTags = this.buildBaseContextTags()

    return this.contextTags
  }

  /**
   * Builds the base client context tags.
   * @returns {string[]} The client context tags.
   */
  private buildBaseContextTags(): string[] {
    const osTag = this.normalizeTagValue(this.browserService.getOS())
    const tags: string[] = [`client:${this.getClientTag()}`, `os:${osTag}`]

    if (this.desktopService.isDesktop) {
      const desktopVersion = this.desktopService.getDesktopVersion() ?? 'unknown'

      tags.push(`version:${this.normalizeVersion(desktopVersion, 2)}`)

      return tags
    }

    const { name, version } = getBrowserInfo()

    tags.push(`browser:${this.normalizeTagValue(name)}`, `version:${this.normalizeVersion(version, 1)}`)

    return tags
  }

  /**
   * Returns the normalized client tag.
   * @returns {string} The client tag.
   */
  private getClientTag(): string {
    return this.desktopService.isDesktop ? 'desktop' : 'browser'
  }

  /**
   * Applies the client context tags to a batch of metrics.
   * @param {MetricPostForm[]} metrics - Metrics to tag.
   * @returns {MetricPostForm[]} Tagged metrics.
   */
  private applyContextTags(metrics: MetricPostForm[]): MetricPostForm[] {
    const contextTags = this.getContextTags()

    return metrics.map(metric => this.applyContextTagsToMetric(metric, contextTags))
  }

  /**
   * Applies context tags to a single metric.
   * @param {MetricPostForm} metric - The metric to tag.
   * @param {string[]} contextTags - The context tags.
   * @returns {MetricPostForm} The tagged metric.
   */
  private applyContextTagsToMetric(metric: MetricPostForm, contextTags: string[]): MetricPostForm {
    const metricTags = metric.tags?.length ? metric.tags.filter(tag => !this.isContextTag(tag)) : []
    const tags = contextTags.length ? [...contextTags, ...metricTags] : metricTags

    if (!tags.length) {
      return { ...metric, tags: undefined }
    }

    return { ...metric, tags }
  }

  /**
   * Determines if a tag is part of the client context.
   * @param {string} tag - The tag value.
   * @returns {boolean} True if the tag is a context tag.
   */
  private isContextTag(tag: string): boolean {
    return (
      tag.startsWith('client:') || tag.startsWith('os:') || tag.startsWith('browser:') || tag.startsWith('version:')
    )
  }

  /**
   * Normalizes tag values to a low-cardinality format.
   * @param {string} value - The raw tag value.
   * @returns {string} The normalized tag value.
   */
  private normalizeTagValue(value: string): string {
    const trimmed = value?.trim?.() ?? ''

    if (!trimmed) {
      return 'unknown'
    }

    return trimmed.toLowerCase().replace(/\s+/g, '-')
  }

  /**
   * Normalizes version strings by keeping a limited number of segments.
   * @param {string} value - The raw version string.
   * @param {number} maxSegments - The maximum number of version segments.
   * @returns {string} The normalized version.
   */
  private normalizeVersion(value: string, maxSegments: number): string {
    const match = value?.match?.(/\d+(\.\d+){0,3}/)

    if (!match) {
      return 'unknown'
    }

    const segments = match[0].split('.').filter(segment => segment.length > 0)

    return segments.slice(0, Math.max(1, maxSegments)).join('.')
  }

  /**
   * Schedules a future queue flush when none is pending.
   */
  private scheduleFlush(): void {
    if (this.flushTimeoutId !== null) {
      return
    }

    this.flushTimeoutId = setTimeout(() => {
      this.flushTimeoutId = null
      this.flushQueue()
    }, METRICS_FLUSH_INTERVAL_MS)
  }

  /**
   * Flushes the queued metrics to the backend.
   */
  private flushQueue(): void {
    if (this.flushInFlight || !this.metricsQueue.length) {
      return
    }

    if (this.flushTimeoutId !== null) {
      clearTimeout(this.flushTimeoutId)
      this.flushTimeoutId = null
    }

    this.flushInFlight = true

    const metrics = this.metricsQueue.splice(0, this.metricsQueue.length)

    this.postMetricsBatches(metrics).subscribe({
      complete: () => this.onFlushComplete(),
      error: () => this.onFlushComplete()
    })
  }

  /**
   * Handles queue state after a flush completes.
   */
  private onFlushComplete(): void {
    this.flushInFlight = false

    if (this.metricsQueue.length >= MAX_METRICS_BATCH_SIZE) {
      this.flushQueue()

      return
    }

    if (this.metricsQueue.length) {
      this.scheduleFlush()
    }
  }

  /**
   * Posts metrics in batches to the tracking endpoint.
   * @param {MetricPostForm[]} metrics - The metrics to post.
   * @returns {Observable<void>} The request observable.
   */
  private postMetricsBatches(metrics: MetricPostForm[]): Observable<void> {
    if (!metrics.length) {
      return of(void 0)
    }

    const batches = this.splitMetrics(metrics, MAX_METRICS_BATCH_SIZE)

    return from(batches).pipe(
      concatMap(batch => this.postBatch({ metrics: batch })),
      ignoreElements(),
      endWith(void 0)
    )
  }

  /**
   * Posts a metrics batch to the tracking endpoint.
   * @param {MetricsBatchPostForm} batch - The metrics batch payload.
   * @returns {Observable<void>} The request observable.
   */
  private postBatch(batch: MetricsBatchPostForm): Observable<void> {
    return this.apiClient.post<void>(this.metricsUrl, batch)
  }

  /**
   * Splits metrics into batches capped by the maximum batch size.
   * @param {MetricPostForm[]} metrics - The metrics to split.
   * @param {number} batchSize - The maximum size per batch.
   * @returns {MetricPostForm[][]} The batches of metrics.
   */
  private splitMetrics(metrics: MetricPostForm[], batchSize: number): MetricPostForm[][] {
    const batches: MetricPostForm[][] = []

    for (let index = 0; index < metrics.length; index += batchSize) {
      batches.push(metrics.slice(index, index + batchSize))
    }

    return batches
  }
}
